import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  User,
  ArrowRight,
  Search,
  Tag,
  Share2,
  BookOpen,
  TrendingUp,
  ChevronRight,
  Filter,
  Eye,
  MessageCircle,
  Star,
  Bookmark,
  Home,
  Sparkles,
  Award,
  Shield,
  Users,
  Building,
  Target,
  Leaf,
} from "lucide-react";

const BlogsPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Actual blog data from your blog posts - ONLY KEEPING EXISTING POSTS
  const blogs = [
    {
      id: 1,
      title: "Mattress Stain Removal: What Actually Works",
      slug: "mattress-stain-removal-what-works",
      excerpt: "Remember that time you knocked over your coffee mug at 6 AM? Or when your toddler had that accident? I've been there. Multiple times. Everyone deals with mattress stains. Here's what actually works.",
      content: "Professional mattress cleaning and stain removal techniques that actually work. From fresh spills to old stains, learn the right methods.",
      image: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=2070&auto=format&fit=crop",
      author: "Sarah Mitchell",
      authorRole: "Senior Cleaning Specialist",
      authorImage: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=2070&auto=format&fit=crop",
      date: "2025-02-24",
      readTime: "8 min read",
      category: "mattress-cleaning",
      categoryName: "Mattress Cleaning",
      tags: ["mattress cleaning", "stain removal", "DIY cleaning", "bedroom maintenance", "cleaning tips"],
      views: 1247,
      comments: 23,
      likes: 89,
      rating: 4.8,
      featured: true,
      popular: true,
    },
    {
      id: 2,
      title: "How to Remove Furniture Marks from Carpets Using Steam Cleaning",
      slug: "remove-furniture-marks-carpets-steam-cleaning",
      excerpt: "That couch sat in the corner for years. Finally moved it last weekend, and guess what? Those nasty indentations are staring right back at me. They won't go away on their own, but carpet steam cleaning can fix them.",
      content: "Learn how to remove those stubborn furniture indentations from your carpets using professional steam cleaning techniques.",
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070&auto=format&fit=crop",
      author: "Michael Chen",
      authorRole: "Carpet Cleaning Expert",
      authorImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop",
      date: "2025-02-20",
      readTime: "6 min read",
      category: "carpet-cleaning",
      categoryName: "Carpet Cleaning",
      tags: ["carpet cleaning", "steam cleaning", "furniture marks", "DIY tips", "carpet maintenance"],
      views: 892,
      comments: 15,
      likes: 67,
      rating: 4.7,
      featured: true,
      popular: false,
    },
    {
      id: 3,
      title: "Deep Cleaning vs End of Lease Cleaning: Key Differences Explained",
      slug: "deep-cleaning-vs-end-of-lease-cleaning-differences",
      excerpt: "Pick the wrong cleaning service and you'll regret it. Could cost you money. Could cost you your rental bond. Could waste your entire weekend. Most people think all cleaning is the same. It's not.",
      content: "Understanding the crucial differences between deep cleaning and end of lease cleaning to save money and protect your bond.",
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop",
      author: "Sarah Mitchell",
      authorRole: "Senior Cleaning Specialist",
      authorImage: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=2070&auto=format&fit=crop",
      date: "2025-02-15",
      readTime: "7 min read",
      category: "end-of-lease",
      categoryName: "End of Lease",
      tags: ["end of lease cleaning", "bond cleaning", "deep cleaning", "moving tips", "rental advice"],
      views: 1563,
      comments: 31,
      likes: 112,
      rating: 4.9,
      featured: true,
      popular: true,
    },
    {
      id: 4,
      title: "10 Clear Signs It's Time to Hire a Professional Cleaning Service",
      slug: "signs-its-time-to-hire-professional-cleaning",
      excerpt: "You know that feeling when cleaning takes over your life? Saturday gets eaten up scrubbing bathrooms and mopping. Sunday? Same story. The dust situation alone is driving you crazy. Here are ten signs you need to call in the professionals.",
      content: "Recognize the telltale signs that it's time to stop DIY cleaning and call in the professionals for a cleaner, healthier home.",
      image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop",
      author: "Emma Watson",
      authorRole: "Cleaning Specialist",
      authorImage: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=2070&auto=format&fit=crop",
      date: "2025-02-10",
      readTime: "9 min read",
      category: "cleaning-tips",
      categoryName: "Cleaning Tips",
      tags: ["professional cleaning", "cleaning services", "home maintenance", "time management", "cleaning tips"],
      views: 2145,
      comments: 42,
      likes: 178,
      rating: 4.9,
      featured: true,
      popular: true,
    }
  ];

  // Calculate category counts
  const categoryCounts = blogs.reduce((acc, blog) => {
    acc[blog.category] = (acc[blog.category] || 0) + 1;
    return acc;
  }, {});

  // Categories with counts - ONLY KEEPING CATEGORIES THAT EXIST
  const categories = [
    { id: "all", name: "All Articles", count: blogs.length },
    { id: "mattress-cleaning", name: "Mattress Cleaning", count: categoryCounts["mattress-cleaning"] || 0 },
    { id: "carpet-cleaning", name: "Carpet Cleaning", count: categoryCounts["carpet-cleaning"] || 0 },
    { id: "end-of-lease", name: "End of Lease", count: categoryCounts["end-of-lease"] || 0 },
    { id: "cleaning-tips", name: "Cleaning Tips", count: categoryCounts["cleaning-tips"] || 0 },
    { id: "health-wellness", name: "Health & Wellness", count: categoryCounts["health-wellness"] || 0 },
  ].filter(category => category.count > 0 || category.id === "all"); // Only show categories with articles

  // Popular tags (extract unique tags from all blogs)
  const allTags = blogs.flatMap(blog => blog.tags);
  const popularTags = [...new Set(allTags)].slice(0, 15);

  // Filter blogs based on category and search
  const filteredBlogs = blogs.filter((blog) => {
    const matchesCategory = activeCategory === "all" || blog.category === activeCategory;
    const matchesSearch = blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         blog.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Featured blogs
  const featuredBlogs = blogs.filter(blog => blog.featured);

  // Popular blogs (sorted by views)
  const popularBlogs = [...blogs].sort((a, b) => b.views - a.views).slice(0, 4);

  // Format date function
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-AU', options);
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      'mattress-cleaning': 'from-purple-500 to-pink-500',
      'carpet-cleaning': 'from-amber-500 to-orange-500',
      'end-of-lease': 'from-rose-500 to-pink-500',
      'cleaning-tips': 'from-blue-500 to-indigo-500',
      'health-wellness': 'from-emerald-500 to-teal-500',
    };
    return colors[category] || 'from-blue-500 to-indigo-500';
  };

  // Blog Listing Schema
  const blogListingSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Knight Cleanings Blog",
    "description": "Expert cleaning tips, DIY guides, and professional advice for Melbourne homes and businesses.",
    "url": "https://knightcleanings.com.au/blog",
    "publisher": {
      "@type": "CleaningService",
      "name": "Knight Cleanings",
      "logo": "https://knightcleanings.com.au/logo.png",
      "url": "https://knightcleanings.com.au",
      "telephone": "+61420499661"
    },
    "blogPost": blogs.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "url": `https://knightcleanings.com.au/blog/${post.slug}`,
      "datePublished": post.date,
      "author": {
        "@type": "Person",
        "name": post.author
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>Cleaning Tips & Advice for Melbourne Homes | Knight Cleanings Blog</title>
        <meta
          name="description"
          content="Read expert cleaning tips, guides, and insights tailored for Melbourne homeowners and businesses from Knight Cleanings. Learn about mattress cleaning, carpet care, stain removal, and more."
        />
        <meta
          name="keywords"
          content="cleaning tips, cleaning blog, home maintenance, stain removal, carpet care, mattress cleaning, end of lease cleaning, professional cleaning advice Melbourne"
        />
        
        {/* Blog Listing Schema */}
        <script type="application/ld+json">
          {JSON.stringify(blogListingSchema)}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://knightcleanings.com.au"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://knightcleanings.com.au/blog"
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800 text-white py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute h-2 w-2 rounded-full bg-white animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <BookOpen size={18} />
              <span className="font-semibold">Expert Cleaning Insights</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Cleaning <span className="bg-gradient-to-r from-amber-300 to-yellow-300 bg-clip-text text-transparent">Blog</span>
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Professional cleaning tips, guides, and industry insights from our team of experts.
              {blogs.length} articles and counting to help you maintain a cleaner, healthier home.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles, tips, and guides..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 pl-12 rounded-full text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <button 
                  onClick={() => setSearchQuery(searchQuery)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 font-bold px-6 py-2 rounded-full hover:from-amber-500 hover:to-yellow-500 transition-all"
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Sidebar */}
            <div className="lg:w-1/4 space-y-8">
              {/* Categories */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Filter size={20} />
                  Categories
                </h3>
                <div className="space-y-3">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center justify-between group ${
                        activeCategory === category.id
                          ? "bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-2 w-2 rounded-full ${
                          activeCategory === category.id ? "bg-blue-600" : "bg-gray-300"
                        }`} />
                        <span className={`font-medium ${
                          activeCategory === category.id ? "text-blue-700" : "text-gray-700"
                        }`}>
                          {category.name}
                        </span>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        activeCategory === category.id
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-600"
                      }`}>
                        {category.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Popular Tags */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Tag size={20} />
                  Popular Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {popularTags.map((tag, index) => (
                    <button
                      key={index}
                      onClick={() => setSearchQuery(tag)}
                      className="px-3 py-2 bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-700 rounded-full text-sm transition-all duration-300 hover:scale-105"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Popular Articles */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <TrendingUp size={20} />
                  Most Popular
                </h3>
                <div className="space-y-4">
                  {popularBlogs.map((blog) => (
                    <div
                      key={blog.id}
                      onClick={() => navigate(`/blog/${blog.slug}`)}
                      className="group cursor-pointer"
                    >
                      <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all duration-300">
                        <div className="h-16 w-16 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={blog.image}
                            alt={blog.title}
                            className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 group-hover:text-blue-600 transition-colors">
                            {blog.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Eye size={12} className="text-gray-400" />
                            <span className="text-xs text-gray-500">{blog.views.toLocaleString()} views</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:w-3/4">
              {/* Featured Articles */}
              {featuredBlogs.length > 0 && searchQuery === '' && activeCategory === 'all' && (
                <div className="mb-12">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Featured Articles</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-8">
                    {featuredBlogs.slice(0, 2).map((blog) => (
                      <div
                        key={blog.id}
                        onClick={() => navigate(`/blog/${blog.slug}`)}
                        className="group cursor-pointer"
                      >
                        <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500">
                          <div className="relative h-64 overflow-hidden">
                            <img
                              src={blog.image}
                              alt={blog.title}
                              className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute top-4 left-4">
                              <span className="bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">
                                FEATURED
                              </span>
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          </div>
                          <div className="p-6">
                            <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                {formatDate(blog.date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={14} />
                                {blog.readTime}
                              </span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                              {blog.title}
                            </h3>
                            <p className="text-gray-600 mb-4 line-clamp-2">{blog.excerpt}</p>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <img
                                  src={blog.authorImage}
                                  alt={blog.author}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{blog.author}</p>
                                  <p className="text-xs text-gray-500">{blog.authorRole}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 text-sm">
                                <span className="flex items-center gap-1 text-amber-500">
                                  <Star size={14} className="fill-current" />
                                  {blog.rating}
                                </span>
                                <span className="flex items-center gap-1 text-gray-500">
                                  <Eye size={14} />
                                  {blog.views.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Articles */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {activeCategory === "all" ? "Latest Articles" : categories.find(c => c.id === activeCategory)?.name}
                  </h2>
                  <div className="text-sm text-gray-600">
                    Showing {filteredBlogs.length} of {blogs.length} articles
                  </div>
                </div>

                {filteredBlogs.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-8">
                    {filteredBlogs.map((blog) => (
                      <div
                        key={blog.id}
                        onClick={() => navigate(`/blog/${blog.slug}`)}
                        className="group cursor-pointer"
                      >
                        <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={blog.image}
                              alt={blog.title}
                              className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute top-4 left-4">
                              <span className={`bg-gradient-to-r ${getCategoryColor(blog.category)} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
                                {blog.categoryName}
                              </span>
                            </div>
                          </div>
                          <div className="p-6">
                            <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {new Date(blog.date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {blog.readTime}
                              </span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                              {blog.title}
                            </h3>
                            <p className="text-gray-600 mb-4 text-sm line-clamp-2">
                              {blog.excerpt}
                            </p>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <img
                                  src={blog.authorImage}
                                  alt={blog.author}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                                <div>
                                  <p className="text-xs font-medium text-gray-900">{blog.author}</p>
                                  <p className="text-xs text-gray-500">{blog.authorRole}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Eye size={12} />
                                  {blog.views.toLocaleString()}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MessageCircle size={12} />
                                  {blog.comments}
                                </span>
                              </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <div className="flex flex-wrap gap-2">
                                {blog.tags.slice(0, 3).map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
                    <div className="h-24 w-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 flex items-center justify-center">
                      <Search size={32} className="text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">No articles found</h3>
                    <p className="text-gray-600 max-w-md mx-auto mb-6">
                      {searchQuery
                        ? `No articles match "${searchQuery}". Try different keywords.`
                        : `No articles in this category yet. Check back soon!`}
                    </p>
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setActiveCategory("all");
                      }}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300"
                    >
                      View All Articles
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Sparkles size={18} />
            <span className="font-semibold">Weekly Cleaning Tips</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Get Expert Cleaning Tips Delivered
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter and receive weekly cleaning tips, exclusive guides,
            and special offers directly to your inbox.
          </p>
          <div className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Enter your email address"
                className="px-6 py-3 rounded-full text-gray-900 w-full focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              <button className="bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-gray-900 font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap">
                Subscribe
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-3">
              No spam ever. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Expert Team Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Meet Our <span className="text-blue-600">Expert Writers</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our articles are written by certified cleaning professionals with years of
              industry experience and specialized training.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                name: "Sarah Mitchell",
                role: "Senior Cleaning Specialist",
                expertise: "Mattress & Upholstery",
                icon: Award,
                color: "from-blue-500 to-indigo-600",
              },
              {
                name: "Michael Chen",
                role: "Carpet Cleaning Expert",
                expertise: "Steam & Deep Cleaning",
                icon: Target,
                color: "from-amber-500 to-orange-600",
              },
              {
                name: "Emma Watson",
                role: "Cleaning Specialist",
                expertise: "Home Organization",
                icon: Home,
                color: "from-emerald-500 to-teal-600",
              },
              {
                name: "Dr. James Wilson",
                role: "Air Quality Specialist",
                expertise: "Health & Hygiene",
                icon: Shield,
                color: "from-purple-500 to-violet-600",
              },
            ].map((expert, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 text-center border border-gray-200 hover:shadow-lg transition-all duration-300"
              >
                <div className={`h-16 w-16 rounded-full bg-gradient-to-r ${expert.color} mx-auto flex items-center justify-center mb-4`}>
                  <expert.icon size={28} className="text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{expert.name}</h3>
                <p className="text-gray-600 text-sm mb-2">{expert.role}</p>
                <p className="text-blue-600 text-sm font-semibold">{expert.expertise}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Professional Cleaning Services?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            While our blog provides expert advice, sometimes you need professional help.
            Our team is ready to handle any cleaning challenge.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/booking"
              className="bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-[1.02] transition-all duration-300 text-lg"
            >
              Book a Service
            </Link>
            <Link
              to="/services"
              className="border-2 border-white/30 hover:border-white hover:bg-white/10 backdrop-blur-sm font-semibold px-8 py-4 rounded-full transition-all duration-300"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={() => window.open('https://wa.me/61420499661?text=Hi%21%20I%20have%20a%20question%20about%20your%20blog%20articles.', '_blank')}
          className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 animate-bounce"
        >
          <MessageCircle size={28} />
        </button>
      </div>
    </div>
  );
};

export default BlogsPage;